import React, { useState, forwardRef } from 'react';
import {
  Group, Burger, Drawer, Stack, Anchor, Box, Image, HoverCard,
} from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import VariableImage from '../../../media/VariableImage';
import PuckInlineRichText from '../../proposal/ProposalRichText/PuckInlineRichText';
import { linkStyleToCss } from './SiteHeaderLinkStyleField';
import SiteHeaderNav, { menuLinkAnchor } from './SiteHeaderNav';
import { resolveButtonLink } from '../../../../shared/boxes/button/buttonLink';
import SiteHeaderPageBar from './SiteHeaderPageBar';
import { useHiddenOnScroll } from './useHiddenOnScroll';
import CtaButton from '../shared/CtaButton';
import { textRoleCss } from '../../../puck/fields/TextRolesField';
import { navRowJustify } from './siteHeaderLayout';
import classes from './SiteHeader.module.css';

/** Marge horizontale par défaut de la barre (40px). */
const DEFAULT_BAR_MARGIN_X = '2.5rem';
/** Marge horizontale fixe en mode hamburger (< sm) : 20px, indépendante du param. */
const HAMBURGER_BAR_MARGIN_X = '1.25rem';

/**
 * Header / Menu — 4 variantes visuelles :
 *   left-logo   : logo à gauche, liens à droite (classique, défaut)
 *   centered    : logo centré, liens en dessous
 *   split       : liens | logo | liens (symétrique)
 *   drawer-left : pas de barre, un ☰ flottant qui ouvre un panneau à gauche
 *
 * navLinks est injecté au runtime par le viewer (pages + sous-menus H2). En
 * éditeur, la config Puck injecte des liens factices (cf. SiteHeader.puck.jsx).
 * `activeSlug` + `activeHighlight` surlignent le lien de la page courante.
 */
export function visibleMenuLinks(navLinks, mode, customLinks = []) {
  if (mode === 'none') return [];
  if (mode === 'custom') {
    return customLinks.map((link, index) => ({
      slug: `lien-${index + 1}`,
      title: link.label,
      anchor: resolveButtonLink(link.target, link.url),
    }));
  }
  if (mode === 'pages-flat') return navLinks.map(({ children, ...link }) => link);
  return navLinks;
}

const SiteHeader = forwardRef(({
  id,
  puck,
  logoUrl = '',
  logoUrlVariable = null,
  logoName = 'Nom du menu',
  backgroundColor = '#ffffff',
  linkStyle = null,
  navVariant = 'left-logo',
  sticky = true,
  navLinks = [],
  accentColor = '',
  logoHeight,
  paddingX,
  activeSlug = '',
  activeHighlight = false,
  /* Style « panneau coulissant » seulement : le bouton flottant se règle à
     part, la couleur de fond du menu allant au panneau qui s'ouvre. */
  burgerBackground = '#ffffff',
  burgerColor = '',
  burgerShadow = true,
  pageBar = false,
  pageBarNumbers = true,
  pageBarBackground = '',
  pageBarActiveBackground = '',
  pageBarActiveColor = '',
  pageBarNumberColor = '',
  pageBarBorders = true,
  pageBarUnderline = true,
  pageBarTextStyle = null,
  /* Liens du menu : toutes les pages avec leurs sous-menus de titres
     (`pages`), les pages seules (`pages-flat`), ou aucun (`none`) quand la
     barre des pages ou le bouton d'action suffisent. */
  menuLinks = 'pages',
  /** Liens du mode `custom` : libellé + cible (même format que le pied de page). */
  customLinks = [],
  /** Menu fixe seulement : il se retire quand on descend, revient quand on
   *  remonte. */
  hideOnScroll = false,
  cta = false,
  ctaLabel = 'Nous contacter',
  ctaLink = undefined,
  ctaColor = '',
  ctaSize = 'sm',
  ctaBorder = undefined,
  ctaTextStyles = null,
}, ref) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isEditing = !!puck?.isEditing;
  const menuNavLinks = visibleMenuLinks(navLinks, menuLinks, customLinks);
  const scrolledAway = useHiddenOnScroll(sticky && hideOnScroll && !isEditing);

  // Style commun des liens (police/taille/gras/italique/souligné/couleur).
  // La couleur passe par une CSS var (pour laisser le hover jouer) ; le reste
  // (typo) s'applique en inline. L'accent (survol/actif) reste séparé.
  const linkColor = linkStyle?.color || '';
  const accent = accentColor || linkColor;
  const linkBaseCss = { ...linkStyleToCss(linkStyle), color: undefined };
  /* L'Anchor Mantine pose sa couleur (`--mantine-color-anchor`, le bleu du
     thème) avec une seule classe, comme `.link` : l'ordre de chargement des
     feuilles décidait, et la couleur des liens se perdait. Régler la variable
     fait dire la même chose aux deux règles ; `.link:hover` garde la main pour
     l'accent. `currentColor` = la couleur héritée, défaut d'origine. */
  const anchorColorVar = { '--mantine-color-anchor': linkColor || 'currentColor' };
  // Dans l'éditeur, aucune page n'est « affichée » : le premier lien en tient
  // lieu, comme dans l'aperçu du menu, pour voir la mise en avant.
  const shownSlug = isEditing ? navLinks[0]?.slug : activeSlug;
  const isActive = (slug) => activeHighlight && !!shownSlug && slug === shownSlug;

  const linkStyleFor = (slug) => (isActive(slug)
    ? { ...linkBaseCss, color: accent, fontWeight: 700 }
    : linkBaseCss);

  const renderDesktopLink = (link) => {
    if (Array.isArray(link.children) && link.children.length > 0) {
      return (
        <HoverCard key={link.slug} width={260} shadow="md" openDelay={120} closeDelay={100}>
          <HoverCard.Target>
            <Group gap={4}>
              <Anchor
                {...menuLinkAnchor(link)}
                className={classes.link}
                style={linkStyleFor(link.slug)}
              >
                {link.title}
              </Anchor>
              <FontAwesomeIcon
                icon={icon({ name: 'chevron-down', style: 'solid' })}
                style={{ fontSize: 14, color: isActive(link.slug) ? accent : (linkColor || undefined) }}
              />
            </Group>
          </HoverCard.Target>
          <HoverCard.Dropdown style={{ overflow: 'hidden' }}>
            <Stack gap={6}>
              {link.children.map((child) => (
                <Anchor
                  key={`${link.slug}-${child.slug}`}
                  href={`#${child.slug}`}
                  size="sm"
                  c="dark"
                  onClick={() => setDrawerOpen(false)}
                >
                  {child.title}
                </Anchor>
              ))}
            </Stack>
          </HoverCard.Dropdown>
        </HoverCard>
      );
    }
    return (
      <Anchor
        key={link.slug}
        {...menuLinkAnchor(link)}
        className={classes.link}
        style={linkStyleFor(link.slug)}
        onClick={() => setDrawerOpen(false)}
      >
        {link.title}
      </Anchor>
    );
  };

  /* « Plus » est un lien du menu : même typographie, même couleur, même mise en
     avant. Il est donc fabriqué ici, avec `linkStyleFor`, et non dans la rangée
     qui ignore tout du style des liens. */
  const renderMoreLabel = (active) => (
    <Group gap={4} wrap="nowrap" style={{ cursor: 'pointer' }}>
      <Anchor
        component="span"
        className={classes.link}
        style={active ? { ...linkBaseCss, color: accent, fontWeight: 700 } : linkBaseCss}
      >
        Plus
      </Anchor>
      <FontAwesomeIcon
        icon={icon({ name: 'chevron-down', style: 'solid' })}
        style={{ fontSize: 14, color: active ? accent : (linkColor || undefined) }}
      />
    </Group>
  );

  const drawerLinks = menuNavLinks.flatMap((link) => {
    const parent = (
      <Anchor
        key={`drawer-${link.slug}`}
        {...menuLinkAnchor(link)}
        className={classes.link}
        style={linkStyleFor(link.slug)}
        onClick={() => setDrawerOpen(false)}
      >
        {link.title}
      </Anchor>
    );
    const children = Array.isArray(link.children)
      ? link.children.map((child) => (
        <Anchor
          key={`drawer-${link.slug}-${child.slug}`}
          href={`#${child.slug}`}
          className={classes.link}
          style={{
            ...linkStyleFor(child.slug),
            color: isActive(child.slug) ? accent : (linkColor || undefined),
            paddingLeft: 16,
            opacity: isActive(child.slug) ? 1 : 0.85,
          }}
          onClick={() => setDrawerOpen(false)}
        >
          {child.title}
        </Anchor>
      ))
      : [];
    return [parent, ...children];
  });

  // Logo image + nom du menu peuvent coexister : image à gauche, nom juste à
  // côté. Le nom est un texte riche (toolbar au survol/sélection) : sa couleur
  // et sa typo sont indépendantes du style des liens.
  const altText = String(logoName || '').replace(/<[^>]+>/g, '').trim() || 'Logo';
  const logoImage = (
    <VariableImage
      blockId={id}
      urlProp="logoUrl"
      src={logoUrl}
      binding={logoUrlVariable}
      isEditing={isEditing}
      renderImage={(src) => src && <Image src={src} alt={altText} h={logoHeight || 36} w="auto" fit="contain" />}
    />
  );
  // Placeholder vide (puce « non affiché sur le site ») géré par PuckInlineRichText
  // dès qu'un placeholder est fourni.
  const logoText = (
    <PuckInlineRichText
      id={id}
      puck={puck}
      html={logoName}
      contentProp="logoName"
      /* Pas de fontWeight de base : le gras se pilote via la toolbar (marque
         <strong>), sinon la toolbar ne le reflète pas et on ne peut pas l'ôter.
         minWidth en édition : garde une zone cliquable même quand le nom est
         vide (sinon le champ collapse à 0 et le placeholder est inatteignable). */
      style={{ fontSize: '1.125rem', lineHeight: 1.2, ...(isEditing ? { minWidth: 120 } : {}) }}
      minHeight={isEditing ? 24 : undefined}
      placeholder="Nom du menu"
      reducedToolbar
    />
  );
  const logoElem = (
    <Group gap="sm" wrap="nowrap" align="center">
      {logoImage}
      {logoText}
    </Group>
  );

  const ctaElem = cta && (
    <CtaButton
      id={id}
      puck={puck}
      label={ctaLabel}
      contentProp="ctaLabel"
      link={ctaLink}
      color={ctaColor || undefined}
      size={ctaSize}
      border={ctaBorder}
      labelStyle={textRoleCss(ctaTextStyles, 'label')}
    />
  );
  const ctaDesktop = ctaElem && <Box visibleFrom="sm" style={{ flexShrink: 0 }}>{ctaElem}</Box>;

  const burger = (drawerLinks.length > 0 || ctaElem) && (
    <Burger
      opened={drawerOpen}
      onClick={() => setDrawerOpen((o) => !o)}
      hiddenFrom="sm"
      size="sm"
      color={linkColor || undefined}
    />
  );

  // Titre du drawer en lecture seule (pas l'éditeur rich text de logoElem, pour
  // éviter deux instances Tiptap sur le même contenu).
  const drawerTitle = (
    <Group gap="sm" wrap="nowrap" align="center">
      {logoImage}
      <span style={{ fontWeight: 700 }}>{altText}</span>
    </Group>
  );
  /* En « panneau coulissant » il n'y a pas de barre : la couleur de fond du
     menu n'a qu'un seul endroit où se poser, le panneau. Les autres styles
     gardent leur tiroir tel qu'il est — leur couleur de fond, elle, est déjà
     prise par la barre. */
  const panelBackground = navVariant === 'drawer-left' ? backgroundColor : undefined;
  const drawer = (
    <Drawer
      opened={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      title={drawerTitle}
      size="xs"
      padding="md"
      withinPortal={false}
      styles={panelBackground ? {
        content: { backgroundColor: panelBackground },
        header: { backgroundColor: panelBackground },
      } : undefined}
    >
      <Stack gap="sm" mt="md">
        {drawerLinks}
        {ctaElem}
      </Stack>
    </Drawer>
  );

  const headerStyle = {
    backgroundColor,
    position: sticky ? 'sticky' : 'relative',
    top: sticky ? 0 : undefined,
    zIndex: sticky ? 100 : undefined,
    boxShadow: sticky ? '0 1px 4px rgba(0,0,0,0.08)' : undefined,
    transform: scrolledAway ? 'translateY(-100%)' : undefined,
    transition: hideOnScroll ? 'transform 250ms ease' : undefined,
    // Couleur des liens exposée via CSS var (pour laisser le hover jouer l'accent).
    '--site-header-text': linkColor || 'inherit',
    '--site-header-accent': accent || 'inherit',
    ...anchorColorVar,
  };

  // Marge horizontale de la barre (pas un padding : à 0 le contenu touche les
  // bords, sans résidu de largeur max). '0rem' reste 0 ; vide → 40px par défaut.
  // En mode hamburger (< sm) : marge fixe 20px, indépendante du paramètre.
  const marginX = paddingX || DEFAULT_BAR_MARGIN_X;
  const constrain = (children) => (
    <Box mx={{ base: HAMBURGER_BAR_MARGIN_X, sm: marginX }} style={{ boxSizing: 'border-box' }}>
      {children}
    </Box>
  );

  /* Le trait qui la sépare du menu court d'un bord à l'autre ; les cases, elles,
     s'alignent sur la marge du menu. Pas en panneau coulissant, qui n'a pas de
     barre. */
  const pageBarElem = pageBar && (
    <Box
      className={classes.pageBarRow}
      px={{ base: HAMBURGER_BAR_MARGIN_X, sm: marginX }}
      style={{ backgroundColor: pageBarBackground || undefined }}
    >
      <SiteHeaderPageBar
        links={navLinks}
        activeSlug={activeSlug}
        isEditing={isEditing}
        showNumbers={pageBarNumbers}
        activeBackground={pageBarActiveBackground}
        activeColor={pageBarActiveColor}
        numberColor={pageBarNumberColor}
        cellBorders={pageBarBorders}
        underline={pageBarUnderline}
        textStyle={pageBarTextStyle}
      />
    </Box>
  );

  /* ── Variante : drawer-left ────────────────────────── */
  if (navVariant === 'drawer-left') {
    return (
      /* Le header reste dans le flux — le viewer mesure sa hauteur pour caler
         le défilement des ancres — mais il ne prend aucune place : le ☰ est
         posé par-dessus le site, et le contenu garde toute la hauteur. */
      <header
        ref={ref}
        className={`${classes.floatingHeader} ${isEditing ? classes.floatingHeaderEditing : ''}`.trim()}
        style={{
          '--site-header-text': linkColor || 'inherit',
          '--site-header-accent': accent || 'inherit',
          ...anchorColorVar,
        }}
      >
        <Box
          className={classes.floatingBurger}
          style={{
            backgroundColor: burgerBackground || 'transparent',
            boxShadow: burgerShadow ? '0 1px 6px rgba(0, 0, 0, 0.18)' : 'none',
          }}
        >
          <Burger
            opened={drawerOpen}
            onClick={() => setDrawerOpen((o) => !o)}
            size="sm"
            color={burgerColor || linkColor || undefined}
            aria-label="Ouvrir le menu"
          />
        </Box>
        {isEditing ? (
          /* Dans l'éditeur, le panneau est montré ouvert : fermé, on ne voyait
             ni le style des liens ni la couleur du panneau, et le clic sur le
             bouton sélectionne le bloc au lieu de l'ouvrir. */
          <Box
            className={classes.drawerPreview}
            style={{ backgroundColor: panelBackground || '#ffffff' }}
          >
            {drawerTitle}
            <Stack gap="sm" mt="md">
              {drawerLinks}
              {ctaElem}
            </Stack>
          </Box>
        ) : drawer}
      </header>
    );
  }

  /* ── Variante : centered ───────────────────────────── */
  if (navVariant === 'centered') {
    return (
      <header ref={ref} className={classes.header} style={headerStyle}>
        {constrain(
          <>
            {/* Desktop : logo centré, liens en dessous — et centrés eux aussi.
                `Stack align="center"` ne centrait que la boîte : dès que la
                rangée occupait toute la largeur, ses liens repartaient à
                gauche. */}
            <Stack gap={4} align="stretch" py="xs" visibleFrom="sm">
              <Group justify="center">{logoElem}</Group>
              <SiteHeaderNav
                links={menuNavLinks}
                renderLink={renderDesktopLink}
                renderMoreLabel={renderMoreLabel}
                isActive={isActive}
                accent={accent}
                justify={navRowJustify('centered')}
              />
              {ctaElem && <Group justify="center" pb={4}>{ctaElem}</Group>}
            </Stack>
            {/* Mobile (hamburger) : logo à gauche, burger à droite (comme left-logo) */}
            <Group justify="space-between" align="center" py="xs" hiddenFrom="sm">
              {logoElem}
              {burger}
            </Group>
          </>,
        )}
        {pageBarElem}
        {drawer}
      </header>
    );
  }

  /* ── Variante : split ──────────────────────────────── */
  if (navVariant === 'split') {
    return (
      <header ref={ref} className={classes.header} style={headerStyle}>
        {constrain(
          <>
            <Group align="center" py="xs" visibleFrom="sm" wrap="nowrap">
              <SiteHeaderNav
                links={menuNavLinks}
                renderLink={renderDesktopLink}
                renderMoreLabel={renderMoreLabel}
                isActive={isActive}
                accent={accent}
                justify={navRowJustify('split')}
                logo={logoElem}
              />
              {ctaDesktop}
            </Group>
            <Group justify="space-between" hiddenFrom="sm" py="xs">
              {logoElem}
              {burger}
            </Group>
          </>,
        )}
        {pageBarElem}
        {drawer}
      </header>
    );
  }

  /* ── Variante par défaut : left-logo ───────────────── */
  return (
    <header ref={ref} className={classes.header} style={headerStyle}>
      {constrain(
        <Group justify="space-between" align="center" py="xs" wrap="nowrap">
          {logoElem}
          <SiteHeaderNav
            links={menuNavLinks}
            renderLink={renderDesktopLink}
            renderMoreLabel={renderMoreLabel}
            isActive={isActive}
            accent={accent}
            justify={navRowJustify('left-logo')}
            visibleFrom="sm"
          />
          {ctaDesktop}
          {burger}
        </Group>,
      )}
      {pageBarElem}
      {drawer}
    </header>
  );
});

export default SiteHeader;
